<template>
    <Teleport to="body">
        <Transition name="edit-window" appear @after-leave="emit('close')">
            <div v-if="visible" class="edit-backdrop" :style="{zIndex:layer}" @mousedown.self="request_close()">
                <div ref="panel" class="edit-panel" :style="{'--ew-width':width+'px'}" role="dialog" aria-modal="true" :aria-label="`${heading}: ${name}`" tabindex="-1">

                    <header class="panel-head">
                        <div class="panel-titles">
                            <span class="panel-heading">{{ heading }}</span>
                            <span class="panel-name">{{ name }}</span>
                        </div>
                        <button class="panel-close" type="button" title="Close (Esc)" aria-label="Close" @click.stop="request_close()">
                            <span class="material-symbols-outlined">close</span>
                        </button>
                    </header>

                    <div class="panel-body">
                        <slot :close="request_close"/>
                    </div>

                    <footer v-if="$slots.footer" class="panel-footer">
                        <slot name="footer" :close="request_close"/>
                    </footer>

                    <footer v-if="held && unsaved.length" class="panel-unsaved" role="alert">
                        <span>Not saved: {{ unsaved.join(', ') }}. Fix or undo it, or close without it.</span>
                        <button type="button" @click.stop="visible=false">Close anyway</button>
                    </footer>

                </div>
            </div>
        </Transition>
    </Teleport>
</template>


<script>
    // Open edit windows, innermost last - Escape closes only the one on top.
    const open_windows=[]
</script>


<script setup>

    import {ref,reactive,computed,watch,provide,nextTick,onMounted,onBeforeUnmount} from 'vue'

    defineProps({
        heading:{type:String,required:true},
        name:{type:String,default:''},
        layer:{type:Number,default:1000},     // above the nav bar (100)
        width:{type:Number,default:760},      // max width in px; phones always get the full screen
    })
    const emit=defineEmits(['close','saved'])

    const id=Symbol()
    let visible=ref(true)
    let panel=ref(null)
    let held=ref(false)         // a close was refused because a change did not save
    let failed=reactive({})     // input -> label, while that input's last save is rejected
    let unsaved=computed(()=>Object.values(failed))
    let pending=new Set()
    let opener=null

    // Every EditField reports its saves here, so closing can wait for them and never drops
    // a change the server rejected without saying so.
    provide('edit_window',{
        track(save){
            pending.add(save)
            save.finally(()=>pending.delete(save))
        },
        // Keyed per input, not per field: two inputs can edit parts of one field, and one
        // saving must not clear the other's rejected change.
        saved(input){
            delete failed[input]
            emit('saved')
        },
        failed(input,label){
            failed[input]=label
        },
        reverted(input){
            delete failed[input]
        },
    })

    watch(unsaved,list=>{
        if(!list.length){
            held.value=false
        }
    })


    async function request_close(){
        // Text fields save on `change`, which only fires on blur: commit the one being typed in.
        if(panel.value && panel.value.contains(document.activeElement)){
            document.activeElement.blur()
        }
        await nextTick()
        await Promise.allSettled([...pending])

        if(unsaved.value.length){
            held.value=true
            return
        }
        visible.value=false
    }


    function on_keydown(event){
        if(event.key=='Escape' && open_windows[open_windows.length-1]==id){
            request_close()
        }
    }


    onMounted(()=>{
        open_windows.push(id)
        document.addEventListener('keydown',on_keydown)
        opener=document.activeElement
        nextTick(()=>panel.value && panel.value.focus())
    })


    onBeforeUnmount(()=>{
        open_windows.splice(open_windows.indexOf(id),1)
        document.removeEventListener('keydown',on_keydown)
        if(opener && document.body.contains(opener)){
            opener.focus()
        }
    })

</script>


<style>
    /* App.vue puts its condensed display font on every element; forms read easier in the
       system UI font, so the edit windows switch back to it (icons keep their own font). */
    .edit-panel,
    .edit-panel *:not(.material-symbols-outlined){
        font-family:system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
    }

    /* App.vue draws icons at weight 600; lighter ones suit the quieter window. */
    .edit-panel .material-symbols-outlined{
        font-variation-settings:'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 20;
    }
</style>


<style scoped>

    .edit-backdrop{
        position:fixed;
        top:0;
        right:0;
        bottom:0;
        left:0;
        display:flex;
        align-items:center;
        justify-content:center;
        padding:16px;
        background:rgba(15,20,30,0.45);
    }

    /* The palette every part of the window reads (sections, fields, the account row). */
    .edit-panel{
        --ew-bg:#f5f3ef;
        --ew-card:#ffffff;
        --ew-line:#e4dfd7;
        --ew-field-line:#d3cdc4;
        --ew-text:#2d2f35;
        --ew-muted:#71747c;
        --ew-focus:#06739e;
        --ew-ok:#2a8a6e;
        --ew-warn:#8a5a00;
        --ew-error:#b3261e;

        display:flex;
        flex-direction:column;
        width:min(var(--ew-width,760px),100%);
        max-height:100%;
        overflow:auto;
        font-size:14px;
        color:var(--ew-text);
        background:var(--ew-bg);
        border-radius:12px;
        box-shadow:0 24px 60px rgba(0,0,0,0.28);
        outline:none;
    }

    .panel-head{
        position:sticky;
        top:0;
        z-index:2;      /* over an open combobox list (1) while the window scrolls */
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:12px;
        padding:18px 22px 14px;
        background:var(--ew-bg);
    }

    .panel-titles{
        display:flex;
        flex-direction:column;
        gap:2px;
        min-width:0;
    }

    .panel-heading{
        font-size:12px;
        letter-spacing:0.06em;
        text-transform:uppercase;
        color:var(--ew-muted);
    }

    .panel-name{
        font-size:20px;
        font-weight:600;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
    }

    .panel-close{
        display:flex;
        align-items:center;
        justify-content:center;
        width:34px;
        height:34px;
        padding:0;
        color:var(--ew-muted);
        background:transparent;
        border:none;
        border-radius:8px;
        box-shadow:none;
    }

    .panel-close:hover{
        color:var(--ew-text);
        background:rgba(0,0,0,0.06);
        box-shadow:none;
    }

    .panel-body{
        display:flex;
        flex-direction:column;
        gap:14px;
        padding:0 22px 22px;
    }

    .panel-unsaved{
        position:sticky;
        bottom:0;
        display:flex;
        flex-wrap:wrap;
        align-items:center;
        justify-content:space-between;
        gap:10px;
        padding:12px 22px;
        color:var(--ew-error);
        background:#fdf1ef;
        border-top:1px solid #f2cfca;
    }

    .panel-unsaved button{
        padding:6px 12px;
        font-size:13px;
        word-break:normal;
        color:var(--ew-error);
        background:#ffffff;
        border:1px solid #e9b8b1;
        border-radius:6px;
        box-shadow:none;
    }

    .panel-unsaved button:hover{
        color:var(--ew-error);
        background:#fbe4e0;
        box-shadow:none;
    }



    .edit-window-enter-active,
    .edit-window-leave-active{
        transition:opacity 0.18s ease-in-out;
    }

    .edit-window-enter-active .edit-panel,
    .edit-window-leave-active .edit-panel{
        transition:transform 0.18s ease-in-out;
    }

    .edit-window-enter-from,
    .edit-window-leave-to{
        opacity:0;
    }

    .edit-window-enter-from .edit-panel,
    .edit-window-leave-to .edit-panel{
        transform:translateY(12px) scale(0.98);
    }

    @media (prefers-reduced-motion:reduce){
        .edit-window-enter-active,
        .edit-window-leave-active,
        .edit-window-enter-active .edit-panel,
        .edit-window-leave-active .edit-panel{
            transition:none;
        }
    }


    .panel-footer{
        position:sticky;
        bottom:0;
        z-index:2;
        display:flex;
        flex-wrap:wrap;
        align-items:center;
        justify-content:flex-end;
        gap:8px;
        padding:12px 22px;
        background:var(--ew-bg);
        border-top:1px solid var(--ew-line);
    }

    /* Phones: the window takes the whole screen, with a bigger close target. */
    @media (max-width:600px){
        .edit-backdrop{ padding:0; }
        .edit-panel{
            width:100%;
            height:100%;
            border-radius:0;
        }
        .panel-head{ padding:12px 12px 10px 16px; }
        .panel-body{ padding:0 12px 16px; }
        .panel-footer{ padding:10px 12px; }
        .panel-close{ width:44px; height:44px; }
    }

</style>
